import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { ChevronLeft, ChevronRight, Command, Menu, Moon, Sun, X } from "lucide-react";
import { CommandPalette } from "@/components/CommandPalette";
import { ConsentSettingsButton } from "@/components/ConsentSettingsButton";
import { categories, featuredCalculators, WORKING_CALCULATOR_COUNT_LABEL } from "@/data/calculators";
import { withTrailingSlash } from "@/lib/urls";

const CAT_COLORS = [
  "#22c55e", "#ef4444", "#3b82f6", "#f97316", "#8b5cf6",
  "#06b6d4", "#10b981", "#f59e0b", "#6366f1", "#ec4899",
  "#84cc16", "#fb923c", "#14b8a6", "#64748b", "#0ea5e9", "#a855f7",
];

const legalPages = [
  { href: withTrailingSlash("/about"), label: "About" },
  { href: withTrailingSlash("/disclaimer"), label: "Disclaimer" },
  { href: withTrailingSlash("/privacy-policy"), label: "Privacy Policy" },
  { href: withTrailingSlash("/terms-and-conditions"), label: "Terms" },
  { href: withTrailingSlash("/legal"), label: "Compliance Hub" },
];

/** Scroll to an in-page section by id. Falls through to native navigation when
 *  the element isn't in the DOM (cross-page navigation). */
export function jumpToId(e: React.MouseEvent<HTMLAnchorElement>, id: string) {
  const el = document.getElementById(id);
  if (el) {
    e.preventDefault();
    el.scrollIntoView({ behavior: "smooth" });
    window.history.replaceState(null, "", `#${id}`);
  }
}

function useDarkMode() {
  const [dark, setDark] = useState(() => {
    try { return localStorage.getItem("ch_dark") === "1"; } catch { return false; }
  });
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    try { localStorage.setItem("ch_dark", dark ? "1" : "0"); } catch { /* */ }
  }, [dark]);
  return [dark, setDark] as const;
}

function PromoBanner() {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <div
      role="banner"
      aria-label="Promotional announcement"
      style={{
        background: "linear-gradient(90deg, #1e40af 0%, #2563eb 60%, #0ea5e9 100%)",
        color: "#fff",
        padding: "0.55rem 1rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.75rem",
        fontSize: "0.875rem",
        fontWeight: 500,
        position: "relative",
        zIndex: 10,
        flexWrap: "wrap",
      }}
    >
      <span style={{ fontSize: "1rem" }}>🧮</span>
      <span>
        <strong>{WORKING_CALCULATOR_COUNT_LABEL} Free Calculators</strong> — finance, health, math, science &amp; more. No signup needed.
      </span>
      <a
        href="/#categories"
        onClick={(e) => jumpToId(e, "categories")}
        style={{
          background: "rgba(255,255,255,0.2)",
          color: "#fff",
          border: "1px solid rgba(255,255,255,0.5)",
          borderRadius: "999px",
          padding: "0.2rem 0.85rem",
          fontWeight: 600,
          fontSize: "0.8rem",
          textDecoration: "none",
          whiteSpace: "nowrap",
          transition: "background 0.2s",
        }}
        onMouseOver={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.35)")}
        onMouseOut={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.2)")}
      >
        Browse All →
      </a>
      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss banner"
        style={{
          position: "absolute",
          right: "0.75rem",
          top: "50%",
          transform: "translateY(-50%)",
          background: "none",
          border: "none",
          color: "rgba(255,255,255,0.75)",
          cursor: "pointer",
          fontSize: "1.1rem",
          lineHeight: 1,
          padding: "0.25rem",
        }}
      >
        ✕
      </button>
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const [dark, setDark] = useDarkMode();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [location] = useLocation();
  const normalizedLocation = location.replace(/\/$/, "") || "/";
  const navRef = useRef<HTMLElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const el = navRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => { el.removeEventListener("scroll", updateScrollState); ro.disconnect(); };
  }, []);

  const scrollNav = (dir: "left" | "right") => {
    navRef.current?.scrollBy({ left: dir === "left" ? -240 : 240, behavior: "smooth" });
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
      if (e.key === "Escape") setMobileMenuOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => { setMobileMenuOpen(false); }, [location]);

  const [showSideRailDebug] = useState(() =>
    import.meta.env.DEV &&
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("sideRailDebug") === "1"
  );

  return (
    <div className="site-shell">
      {showSideRailDebug && (
        <div className="side-rail-debug" aria-hidden="true">
          <div className="side-rail-debug__rail side-rail-debug__rail--left">160 × 600 side rail</div>
          <div className="side-rail-debug__rail side-rail-debug__rail--right">160 × 600 side rail</div>
        </div>
      )}
      <a href="#main-content" className="skip-to-content">Skip to main content</a>
      <header className="site-header" role="banner">
        <Link href="/" className="brand" aria-label="United Calculator home">
          <span className="brand-mark">
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
              <rect x="1.5" y="1.5" width="19" height="19" rx="3.5" stroke="white" strokeWidth="1.4" strokeOpacity="0.5"/>
              <rect x="4" y="4" width="7" height="4.5" rx="1.2" fill="white"/>
              <rect x="4" y="11" width="3.5" height="3" rx="0.7" fill="white" fillOpacity="0.65"/>
              <rect x="4" y="15.5" width="3.5" height="2.5" rx="0.7" fill="white" fillOpacity="0.65"/>
              <rect x="9.5" y="11" width="3" height="3" rx="0.7" fill="white" fillOpacity="0.65"/>
              <rect x="9.5" y="15.5" width="3" height="2.5" rx="0.7" fill="white" fillOpacity="0.65"/>
              <rect x="15" y="11" width="3" height="7" rx="0.7" fill="#38bdf8"/>
            </svg>
          </span>
          <span className="brand-text">
            <span className="brand-wordmark">
              <span className="brand-united">United</span><span className="brand-calculator">Calculator</span>
            </span>
          </span>
        </Link>
        <nav aria-label="Primary navigation">
          <a href="/#categories" onClick={(e) => jumpToId(e, "categories")}>Categories</a>
          <a href="/#popular" onClick={(e) => jumpToId(e, "popular")}>Popular</a>
          <Link href={withTrailingSlash("/blog")}>Blog</Link>
          <Link href={withTrailingSlash("/about")}>About</Link>
        </nav>
        <div className="header-controls">
          <button
            className="header-icon-btn"
            onClick={() => setPaletteOpen(true)}
            aria-label="Open search (⌘K)"
            title="Search calculators (⌘K)"
          >
            <Command size={18} />
            <span className="header-icon-label">⌘K</span>
          </button>
          <button
            className="header-icon-btn"
            onClick={() => setDark((d) => !d)}
            aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
            title={dark ? "Light mode" : "Dark mode"}
          >
            {dark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button
            className="header-icon-btn mobile-menu-btn"
            onClick={() => setMobileMenuOpen((o) => !o)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </header>

      {mobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)} aria-hidden="true" />
      )}
      <nav
        className={`mobile-menu-drawer${mobileMenuOpen ? " open" : ""}`}
        aria-label="Mobile navigation"
        aria-hidden={!mobileMenuOpen}
      >
        <div className="mobile-menu-header">
          <span className="mobile-menu-title">All Categories</span>
          <button className="mobile-menu-close" onClick={() => setMobileMenuOpen(false)} aria-label="Close menu">
            <X size={20} />
          </button>
        </div>
        <div className="mobile-menu-cats">
          {categories.map((cat, i) => (
            <Link
              key={cat.slug}
              href={withTrailingSlash(`/category/${cat.slug}`)}
              className={`mobile-menu-cat${normalizedLocation === `/category/${cat.slug}` ? " active" : ""}`}
            >
              <span className="cat-dot" style={{ background: CAT_COLORS[i] }} />
              {cat.name}
            </Link>
          ))}
        </div>
        <div className="mobile-menu-footer">
          <a href="/#categories" onClick={(e) => { jumpToId(e, "categories"); setMobileMenuOpen(false); }}>Browse All</a>
          <a href="/#popular" onClick={(e) => { jumpToId(e, "popular"); setMobileMenuOpen(false); }}>Popular</a>
          <Link href={withTrailingSlash("/blog")}>Blog</Link>
          <Link href="/about">About</Link>
        </div>
      </nav>

      <div className="cat-nav-wrap">
        {canScrollLeft && (
          <button className="cat-nav-arrow cat-nav-arrow-left" onClick={() => scrollNav("left")} aria-label="Scroll categories left">
            <ChevronLeft size={16} />
          </button>
        )}
        <nav className="cat-nav" ref={navRef} aria-label="Calculator categories">
          <div className="cat-nav-inner">
            {categories.map((cat, i) => (
              <Link
                key={cat.slug}
                href={withTrailingSlash(`/category/${cat.slug}`)}
                className={`cat-nav-link${normalizedLocation === `/category/${cat.slug}` ? " active" : ""}`}
              >
                <span className="cat-dot" style={{ background: CAT_COLORS[i] }} />
                {cat.name}
              </Link>
            ))}
          </div>
        </nav>
        {canScrollRight && (
          <button className="cat-nav-arrow cat-nav-arrow-right" onClick={() => scrollNav("right")} aria-label="Scroll categories right">
            <ChevronRight size={16} />
          </button>
        )}
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <PromoBanner />

      <div id="main-content" tabIndex={-1}>{children}</div>

      <footer className="site-footer" role="contentinfo">
        <div className="footer-brand">
          <Link href="/" className="footer-logo">
            <span className="brand-mark">
              <svg width="18" height="18" viewBox="0 0 22 22" fill="none" aria-hidden="true">
                <rect x="4" y="4" width="7" height="4.5" rx="1.2" fill="white"/>
                <rect x="4" y="11" width="3.5" height="3" rx="0.7" fill="white" fillOpacity="0.7"/>
                <rect x="4" y="15.5" width="3.5" height="2.5" rx="0.7" fill="white" fillOpacity="0.7"/>
                <rect x="9.5" y="11" width="3" height="3" rx="0.7" fill="white" fillOpacity="0.7"/>
                <rect x="9.5" y="15.5" width="3" height="2.5" rx="0.7" fill="white" fillOpacity="0.7"/>
                <rect x="15" y="11" width="3" height="7" rx="0.7" fill="#38bdf8"/>
              </svg>
            </span>
            <strong>United Calculator</strong>
          </Link>
          <p className="footer-desc">{WORKING_CALCULATOR_COUNT_LABEL} free online calculators for finance, health, math, science, construction, business, sports and everyday life — no signup required.</p>
          <p className="footer-disclaimer">Disclaimer: All calculators are provided for informational and educational purposes only. Results are estimates and should not substitute professional financial, medical, legal, or tax advice. Always verify with a qualified professional before acting.</p>
        </div>

        <div className="footer-nav">
          <div className="footer-col">
            <h4>Categories A–H</h4>
            {categories.slice(0, 8).map((cat) => (
              <Link key={cat.slug} href={withTrailingSlash(`/category/${cat.slug}`)}>{cat.name}</Link>
            ))}
          </div>
          <div className="footer-col">
            <h4>Categories M–T</h4>
            {categories.slice(8).map((cat) => (
              <Link key={cat.slug} href={withTrailingSlash(`/category/${cat.slug}`)}>{cat.name}</Link>
            ))}
          </div>
          <div className="footer-col">
            <h4>Site</h4>
            <Link href="/">Home</Link>
            <Link href={withTrailingSlash("/blog")}>Blog &amp; Guides</Link>
            {legalPages.map((page) => <Link key={page.href} href={page.href}>{page.label}</Link>)}
            <h4 style={{ marginTop: "1rem" }}>Popular tools</h4>
            <Link href={withTrailingSlash("/mortgage-calculator")}>Mortgage Calculator</Link>
            {featuredCalculators.slice(0, 4).map((calc) => (
              <Link key={calc.slug} href={withTrailingSlash(`/calculator/${calc.slug}`)}>{calc.title}</Link>
            ))}
          </div>
        </div>

        <div className="footer-bottom">
          <span>© {new Date().getFullYear()} United Calculator. Free to use.</span>
          <ConsentSettingsButton />
        </div>
      </footer>
    </div>
  );
}
